/**
 * Nested exclusive pickers: each plan type owns its periods.
 * Strategic / Annual show that plan’s years; Quarterly shows Q1–Q4.
 * Switching to Strategic or Annual lands on the current year when it exists;
 * switching to Quarterly lands on the last completed quarter.
 */

import React from 'react';
import {
  currentCalendarYear,
  lastCompletedQuarter,
  type CompareHorizon,
  type CompareOptions,
  type ComparePrefs
} from './types';

const HORIZONS: CompareHorizon[] = ['STRATEGIC', 'ANNUAL', 'QUARTERLY'];

interface CompareControlsProps {
  compare: ComparePrefs;
  options: CompareOptions;
  saving: boolean;
  onChange: (next: ComparePrefs) => void;
  t: (key: string) => string;
}

function CheckRow({
  label,
  checked,
  disabled,
  onPick
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <label className="flex items-center gap-1 text-[11px] text-[var(--color-surface-text)] cursor-pointer">
      <input type="checkbox" className="mt-0" disabled={disabled} checked={checked} onChange={onPick} />
      <span className="leading-tight">{label}</span>
    </label>
  );
}

/** Current year when it is on the plan; otherwise the nearest year on the plan. */
function defaultYearOnPlan(years: number[]): number {
  const current = currentCalendarYear();
  if (years.includes(current)) return current;
  const first = years[0];
  const last = years[years.length - 1];
  if (first == null || last == null) return current;
  if (current < first) return first;
  return last;
}

function pickQuarter(quarters: number[], year: number): number {
  const last = lastCompletedQuarter();
  const preferred = last.year === year ? last.quarter : last.year < year ? 4 : 1;
  if (quarters.includes(preferred)) return preferred;
  return quarters[quarters.length - 1] ?? preferred;
}

export const CompareControls: React.FC<CompareControlsProps> = ({
  compare,
  options,
  saving,
  onChange,
  t
}) => {
  const labels: Record<CompareHorizon, string> = {
    STRATEGIC: t('kpi.perfHorizonStrategic') || 'Strategic',
    ANNUAL: t('kpi.perfHorizonAnnual') || 'Annual',
    QUARTERLY: t('kpi.perfHorizonQuarterly') || 'Quarterly'
  };
  const quarterLabels: Record<number, string> = {
    1: t('kpi.perfQuarter1') || 'Q1',
    2: t('kpi.perfQuarter2') || 'Q2',
    3: t('kpi.perfQuarter3') || 'Q3',
    4: t('kpi.perfQuarter4') || 'Q4'
  };

  const yearsFor = (horizon: CompareHorizon): number[] => {
    if (horizon === 'STRATEGIC') return options.strategicYears;
    if (horizon === 'ANNUAL') return options.annualYears;
    return [options.quarterlyYear];
  };

  const selectHorizon = (horizon: CompareHorizon) => {
    if (compare.horizon === horizon) return;
    if (horizon === 'QUARTERLY') {
      onChange({
        horizon,
        year: options.quarterlyYear,
        quarter: pickQuarter(options.quarters, options.quarterlyYear)
      });
      return;
    }
    onChange({
      horizon,
      year: defaultYearOnPlan(yearsFor(horizon)),
      quarter: compare.quarter
    });
  };

  const selectYear = (horizon: CompareHorizon, year: number) => {
    if (compare.horizon === horizon && compare.year === year) return;
    onChange({ horizon, year, quarter: compare.quarter });
  };

  const selectQuarter = (quarter: number) => {
    if (compare.horizon === 'QUARTERLY' && compare.quarter === quarter) return;
    onChange({ horizon: 'QUARTERLY', year: options.quarterlyYear, quarter });
  };

  return (
    <div className="mb-2">
      <p className="text-[10px] text-[var(--color-secondary-text)] mb-0.5">
        {t('kpi.perfCompareLabel') || 'Plan'}
      </p>
      <div className="space-y-1">
        {HORIZONS.map((horizon) => (
          <div key={horizon} className="grid grid-cols-[5.25rem_1fr] gap-x-2 items-start">
            <CheckRow
              label={labels[horizon]}
              checked={compare.horizon === horizon}
              disabled={saving}
              onPick={() => selectHorizon(horizon)}
            />
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              {horizon === 'QUARTERLY'
                ? options.quarters.map((quarter) => (
                    <CheckRow
                      key={quarter}
                      label={quarterLabels[quarter] || `Q${quarter}`}
                      checked={compare.horizon === 'QUARTERLY' && compare.quarter === quarter}
                      disabled={saving}
                      onPick={() => selectQuarter(quarter)}
                    />
                  ))
                : yearsFor(horizon).map((year) => (
                    <CheckRow
                      key={year}
                      label={String(year)}
                      checked={compare.horizon === horizon && compare.year === year}
                      disabled={saving}
                      onPick={() => selectYear(horizon, year)}
                    />
                  ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
